import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { selectToken } from "../../auth/auth.selector";
import {
  convertStraight,
  convertToYYYYMMDD,
  currentMonthCurrentDate,
} from "../../common-data/Converts";
import { fetchApiData } from "../../../redux/reducers/commonApiSlice";
import { checkinReports } from "../../../data/ApiEndpoints";
import ReactVortoxSpinner from "../../common-components/ReactVortoxSpinner";
import OnlyDateFilter from "../../pt-trials/common/OnlyDateFilter";
import PaymentLinkTable from "../../payment-link-system/PaymentLinkTable";

const CheckinReports = ({
  clickValues,
  sendableStatsQPs,
  MLCDate,
  MCLDate,
  lastCheckinTRDate,
}) => {
  const token = useSelector(selectToken);
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.common);

  const queryParams = new URLSearchParams(sendableStatsQPs);
  const startDateParam = queryParams.get("start_date");
  const endDateParam = queryParams.get("end_date");

  const [isPage, setIsPage] = useState(0);
  const iniAllFilters = {
    selectedStartDate: startDateParam
      ? moment(startDateParam).tz("Asia/Kolkata").format("DD-MM-YYYY")
      : convertStraight(currentMonthCurrentDate),
    selectedEndDate: endDateParam
      ? moment(endDateParam).tz("Asia/Kolkata").format("DD-MM-YYYY")
      : convertStraight(currentMonthCurrentDate),
  };
  const [allFilters, setAllFilters] = useState(iniAllFilters);
  const initialFilters = {
    page: isPage + 1,
    limit: 10,
    user_id: clickValues?.userId,
    date_from: allFilters.selectedStartDate
      ? convertToYYYYMMDD(allFilters.selectedStartDate)
      : "",
    date_to: allFilters.selectedEndDate
      ? convertToYYYYMMDD(allFilters.selectedEndDate)
      : "",
  };
  const [filters, setFilters] = useState(initialFilters);
  const iniTbDatas = {
    allData: null,
    pagination: [],
  };
  const [tbDatas, setTbDatas] = useState(iniTbDatas);

  const onClickReset = () => {
    setFilters(initialFilters);
    setAllFilters(iniAllFilters);
  };

  useEffect(() => {
    setAllFilters((prevs) => ({
      ...prevs,
      selectedStartDate: startDateParam
        ? moment(startDateParam).tz("Asia/Kolkata").format("DD-MM-YYYY")
        : convertStraight(currentMonthCurrentDate),
      selectedEndDate: endDateParam
        ? moment(endDateParam).tz("Asia/Kolkata").format("DD-MM-YYYY")
        : convertStraight(currentMonthCurrentDate),
    }));
  }, [startDateParam, endDateParam]);

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      user_id: clickValues?.userId,
      date_from: allFilters.selectedStartDate
        ? convertToYYYYMMDD(allFilters.selectedStartDate)
        : "",
      date_to: allFilters.selectedEndDate
        ? convertToYYYYMMDD(allFilters.selectedEndDate)
        : "",
    }));
  }, [allFilters]);

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      page: isPage + 1,
    }));
  }, [isPage]);

  useEffect(() => {
    if (filters.user_id && filters.date_from && filters.date_to) {
      dispatch(
        fetchApiData({
          method: "GET",
          endpoint: checkinReports,
          payload: {
            filters: filters,
          },
          token: token,
        })
      );
    }
  }, [filters]);

  useEffect(() => {
    if (data?.status && data?.data?.length > 0) {
      setTbDatas((prevs) => ({
        ...prevs,
        allData: data.data || [],
        pagination: data?.pagination || [],
      }));
    } else {
      setTbDatas(iniTbDatas);
    }
  }, [data]);

  const tbColumns = [
    {
      dataField: "gym_name",
      text: "gym name",
      formatter: (cell, row) => {
        return (
          <span className="tb-data minWidth150">{row?.gym_name || "NA"}</span>
        );
      },
    },
    {
      dataField: "name",
      text: "name",
      formatter: (cell, row) => {
        return (
          <span className="tb-data">
            {row?.name || "NA"}
            <br />
            <small style={{ textTransform: "lowercase" }}>
              {row?.mobile || ""}
              <br />
              {row?.email || ""}
            </small>
          </span>
        );
      },
    },
    {
      dataField: "shift_details",
      text: "Shift Timing",
      formatter: (cell, row) => {
        const shiftTimings = row?.shift_details?.map((shift) => {
          const startTime = shift?.start_time
            ? moment(shift.start_time, "HH:mm").tz("Asia/Kolkata").format("h:mmA")
            : "";
          const endTime = shift?.end_time
            ? moment(shift.end_time, "HH:mm").tz("Asia/Kolkata").format("h:mmA")
            : "";
          return `${startTime}-${endTime}`;
        });

        const shiftTiming = shiftTimings
          ? shiftTimings?.join(", ")
          : "No Shift Found";

        return (
          <span className="tb-data minWidth150">{shiftTiming || "-"}</span>
        );
      },
    },
    {
      dataField: "attendance_details",
      text: "Date",
      formatter: (cell, row) => {
        const checkinDate =
          row?.attendance_details?.length > 0 ? row.attendance_details[0] : "-";
        return (
          <span className="tb-data minWidth100">
            {checkinDate?.date || "-"}
          </span>
        );
      },
    },
    {
      dataField: "attendance_details",
      text: "Check-in",
      formatter: (cell, row) => {
        const checkinTimes = row?.attendance_details
          ?.filter((entry) => entry.mode === "in")
          ?.map((entry) => entry.time);
        const checkinTime =
          checkinTimes?.length > 0 ? checkinTimes?.join(", ") : "-";
        return <span className="tb-data minWidth100">{checkinTime}</span>;
      },
    },
    {
      dataField: "attendance_details",
      text: "Check-out",
      formatter: (cell, row) => {
        const checkoutTimes = row?.attendance_details
          ?.filter((entry) => entry?.mode === "out")
          ?.map((entry) => entry?.time);
        const checkoutTime =
          checkoutTimes?.length > 0 ? checkoutTimes?.join(", ") : "-";
        return <span className="tb-data minWidth100">{checkoutTime}</span>;
      },
    },
  ];
  return (
    <>
      <OnlyDateFilter
        filters={allFilters}
        setFilters={setAllFilters}
        onClickReset={onClickReset}
        MLCDate={MLCDate}
        MCLDate={MCLDate}
        lastCheckinTRDate={lastCheckinTRDate}
      />

      <div className="fullWidthFlexCenter overflowAuto separatedTable">
        {loading ? (
          <ReactVortoxSpinner />
        ) : (
          <PaymentLinkTable
            tableData={tbDatas.allData}
            tableColumns={tbColumns}
            tablePagination={tbDatas.pagination}
            isPage={isPage}
            setIsPage={(value) => setIsPage(value)}
          />
        )}
      </div>
    </>
  );
};

export default CheckinReports;
